'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { supabase } from '@/lib/supabase';

interface LoginFormData {
  email: string;
  password: string;
}

export default function SellerLoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const router = useRouter();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<LoginFormData>({ mode: 'onBlur' });

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    try {
      // Dynamically use the current origin so the redirect works on both
      // the preview domain and the production domain (www.bozaat.com)
      const origin = typeof window !== 'undefined'
        ? window.location.origin
        : (process.env.NEXT_PUBLIC_SITE_URL || 'https://bozaat5282.builtwithrocket.new');

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${origin}/auth/callback?next=/seller-dashboard`,
        },
      });
      if (error) {
        toast.error(error.message || 'Google sign-in failed. Please try again.');
        setIsGoogleLoading(false);
      }
      // On success, browser redirects — no further action needed
    } catch (err) {
      console.error('Google sign-in error:', err);
      toast.error('Something went wrong. Please try again.');
      setIsGoogleLoading(false);
    }
  };

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email: data.email.trim().toLowerCase(),
        password: data.password,
      });

      if (error) {
        setIsLoading(false);
        // Map common Supabase auth errors to user-friendly messages
        let message = 'Invalid email or password. Please try again.';
        if (error.message?.toLowerCase().includes('email not confirmed')) {
          message = 'Please verify your email address before logging in.';
        } else if (error.message?.toLowerCase().includes('invalid login credentials')) {
          message = 'Invalid email or password. Please check your credentials and try again.';
        } else if (error.message) {
          message = error.message;
        }
        setError('root', { message });
        return;
      }

      if (!authData.user) {
        setIsLoading(false);
        setError('root', { message: 'Login failed. Please try again.' });
        return;
      }

      // Check seller role — but allow login even if profile row is missing
      // (handles Flutter-registered users whose profile may use a different structure)
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('role, onboarding_status')
        .eq('id', authData.user.id)
        .single();

      if (profileError && profileError.code !== 'PGRST116') {
        // PGRST116 = row not found — that's acceptable, proceed to dashboard
        console.warn('Profile fetch warning:', profileError.message);
      }

      // If admin role, redirect to admin dashboard
      if (profile?.role === 'admin') {
        // Also check admin_user_roles as fallback
        toast.success('Logged in as Admin!');
        router.push('/admin/dashboard');
        return;
      }

      // Check admin_user_roles table as secondary check
      const { data: adminRole } = await supabase
        .from('admin_user_roles')
        .select('id')
        .eq('user_id', authData.user.id)
        .maybeSingle();

      if (adminRole) {
        toast.success('Logged in as Admin!');
        router.push('/admin/dashboard');
        return;
      }

      // Only block login if profile explicitly has a non-seller role
      if (profile && profile.role && profile.role !== 'seller') {
        await supabase.auth.signOut();
        setIsLoading(false);
        setError('root', {
          message: 'This account is not registered as a seller. Please sign up as a seller.',
        });
        return;
      }

      toast.success('Logged in successfully!');
      router.push('/seller-dashboard');
    } catch (err) {
      console.error('Login error:', err);
      setIsLoading(false);
      setError('root', { message: 'Something went wrong. Please try again.' });
    }
  };

  return (
    <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden">
      <div className="p-8 space-y-5">
        {/* Google Sign-In */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={isGoogleLoading || isLoading}
          className="w-full flex items-center justify-center gap-3 border border-input rounded-xl px-4 py-3 text-sm font-600 text-foreground bg-card hover:bg-muted transition-colors disabled:opacity-70 disabled:cursor-not-allowed min-h-[46px]"
        >
          {isGoogleLoading ? (
            <>
              <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
              Connecting to Google…
            </>
          ) : (
            <>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M17.64 9.2045C17.64 8.5663 17.5827 7.9527 17.4764 7.3636H9V10.845H13.8436C13.635 11.97 13.0009 12.9231 12.0477 13.5613V15.8195H14.9564C16.6582 14.2527 17.64 11.9454 17.64 9.2045Z" fill="#4285F4"/>
                <path d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5613C11.2418 14.1013 10.2109 14.4204 9 14.4204C6.65591 14.4204 4.67182 12.8372 3.96409 10.71H0.957275V13.0418C2.43818 15.9831 5.48182 18 9 18Z" fill="#34A853"/>
                <path d="M3.96409 10.71C3.78409 10.17 3.68182 9.5931 3.68182 9C3.68182 8.4069 3.78409 7.83 3.96409 7.29V4.9582H0.957275C0.347727 6.1731 0 7.5477 0 9C0 10.4523 0.347727 11.8269 0.957275 13.0418L3.96409 10.71Z" fill="#FBBC05"/>
                <path d="M9 3.5795C10.3214 3.5795 11.5077 4.0336 12.4405 4.9255L15.0218 2.3441C13.4632 0.8918 11.4259 0 9 0C5.48182 0 2.43818 2.0168 0.957275 4.9582L3.96409 7.29C4.67182 5.1627 6.65591 3.5795 9 3.5795Z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </>
          )}
        </button>

        {/* Divider */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground font-500">or login with email</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
          {/* Root error */}
          {errors.root && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-start gap-2.5">
              <Icon name="ExclamationTriangleIcon" size={16} className="text-error flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{errors.root.message}</p>
            </div>
          )}

          {/* Email */}
          <div>
            <label className="block text-sm font-600 text-foreground mb-1.5">
              Email Address
            </label>
            <input
              type="email"
              placeholder="rahul@yourstore.in"
              autoComplete="email"
              className="w-full border border-input rounded-xl px-4 py-2.5 text-sm text-foreground bg-card input-focus placeholder:text-muted-foreground"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Enter a valid email address',
                },
              })}
            />
            {errors.email && (
              <p className="text-xs text-error mt-1.5 flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={12} />
                {errors.email.message}
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-600 text-foreground">Password</label>
              <Link
                href="/password-reset"
                className="text-xs text-primary font-500 hover:underline"
              >
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Your password"
                autoComplete="current-password"
                className="w-full border border-input rounded-xl px-4 py-2.5 pr-11 text-sm text-foreground bg-card input-focus placeholder:text-muted-foreground"
                {...register('password', {
                  required: 'Password is required',
                })}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
              </button>
            </div>
            {errors.password && (
              <p className="text-xs text-error mt-1.5 flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={12} />
                {errors.password.message}
              </p>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading || isGoogleLoading}
            className="btn-primary w-full py-3 rounded-xl text-sm font-600 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-h-[46px]"
          >
            {isLoading ? (
              <>
                <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                Logging in…
              </>
            ) : (
              'Login to Seller Dashboard'
            )}
          </button>

          {/* Signup link */}
          <p className="text-center text-sm text-muted-foreground">
            Don&apos;t have an account?{' '}
            <Link href="/seller-sign-up" className="text-primary font-600 hover:underline">
              Sign up as seller
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}